import { forwardRef, useEffect, useCallback, useImperativeHandle, useState, useRef } from "react";
import { Container, Alert, Button, Card, Row, Col, ListGroup, Form } from "react-bootstrap";
import Plotly from "plotly.js-dist";

const validDensityTypes = ["histogram2d", "density"];

const ErrorGraph = forwardRef(
    (
        {
            measurement = {},
            errorGraphData,
            showGraph,
            loading,
            error,
            onClose,
            onRetry,
            customization,
            getColorPalette,
            applyTheme,
            onDegreeChange,
            initialDegree = 2,
            auth,
        },
        ref
    ) => {
        const plotDivRef = useRef(null);
        const [degree, setDegree] = useState(initialDegree);
        const [authError, setAuthError] = useState(null);
        const [renderError, setRenderError] = useState(null);

        // Validate auth token
        useEffect(() => {
            if (!auth?.token) {
                setAuthError("Authentication token missing");
            } else {
                setAuthError(null);
            }
        }, [auth?.token]);

        // Export graph as image
        const exportGraph = useCallback(
            (format) => {
                if (!measurement?.id || !plotDivRef.current) return;
                Plotly.downloadImage(plotDivRef.current, {
                    format,
                    width: 1200,
                    height: 800,
                    filename: `error-graph-for-${ measurement.filename || "measurement" }`,
                });
            },
            [measurement?.id]
        );

        // Export data as CSV
        const exportData = useCallback(() => {
            if (!errorGraphData || !measurement?.id) return;
            let csvContent = "data:text/csv;charset=utf-8,Sensor,Temperature,Error\n";
            Object.entries(errorGraphData).forEach(
                ([sensorKey, sensorData]) => {
                    if (sensorData.data?.x && sensorData.data?.y) {
                        sensorData.data.x.forEach((temp, idx) => {
                            const errorVal = sensorData.data.y[idx];
                            if (temp !== undefined && errorVal !== undefined) {
                                csvContent += `${sensorKey},${temp.toFixed(2)},${errorVal.toFixed(4)}\n`;
                            }
                        });
                    }
                }
            );
            const encodedUri = encodeURI(csvContent);
            const link = document.createElement("a");
            link.setAttribute("href", encodedUri);
            link.setAttribute("download", `error-data-for-${measurement.filename || "measurement"}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }, [errorGraphData, measurement?.id]);

        useImperativeHandle(ref, () => ({
            exportGraph,
            exportData,
        }));

        // Handle degree change
        const handleDegreeChange = useCallback((e) => {
                const newDegree = parseInt(e.target.value, 10);
                if (!isNaN(newDegree) && newDegree >= 1 && newDegree <= 4) {
                    setDegree(newDegree);
                    if (onDegreeChange) { onDegreeChange(newDegree) }
                }
            },
            [onDegreeChange]
        );

        // Render Plotly graph
        useEffect(() => {
            if ( !errorGraphData || !showGraph || !measurement?.id || !plotDivRef.current || Object.keys(errorGraphData).length === 0 ) {return}

            const plotData = [];
            const colors = getColorPalette();
            const sensorKeys = Object.keys(errorGraphData);

            const config = {
                responsive: true,
                displayModeBar: true,
                modeBarButtonsToAdd: [
                    { name: "Download as PNG", icon: Plotly.Icons.camera, click: () => exportGraph("png") },
                    { name: "Download as SVG", icon: Plotly.Icons.camera, click: () => exportGraph("svg") },
                ],
                scrollZoom: true,
            };

            // Calculate global min/max for axes
            let globalMinX = Infinity;
            let globalMaxX = -Infinity;
            let globalMinY = Infinity;
            let globalMaxY = -Infinity;

            Object.values(errorGraphData).forEach((sensorData) => {
                if (sensorData.data?.x && sensorData.data?.y) {
                    const xValues = sensorData.data.x.filter((x) => !isNaN(x));
                    const yValues = sensorData.data.y.filter((y) => !isNaN(y));
                    if (xValues.length > 0) {
                        globalMinX = Math.min(globalMinX, Math.min(...xValues));
                        globalMaxX = Math.max(globalMaxX, Math.max(...xValues));
                    }
                    if (yValues.length > 0) {
                        globalMinY = Math.min(globalMinY, Math.min(...yValues));
                        globalMaxY = Math.max(globalMaxY, Math.max(...yValues));
                    }
                }
            });

            // Add padding to axes
            const xPadding = (globalMaxX - globalMinX) * 0.05 || 1;
            const yPadding = (globalMaxY - globalMinY) * 0.05 || 0.01;
            const minXtoShow = isFinite(globalMinX) ? globalMinX - xPadding : 0;
            const maxXtoShow = isFinite(globalMaxX) ? globalMaxX + xPadding : 100;
            const minYtoShow = isFinite(globalMinY) ? globalMinY - yPadding : -0.1;
            const maxYtoShow = isFinite(globalMaxY) ? globalMaxY + yPadding : 0.1;

            try {
                if (customization.graphType === "facet") {
                    const columns = customization.facetColumns || 2;
                    const rows = Math.ceil(sensorKeys.length / columns);
                    const grid = { rows, columns, pattern: "independent" };

                    sensorKeys.forEach((sensorKey, index) => {
                        const sensorData = errorGraphData[sensorKey];
                        if (!sensorData.data?.x || !sensorData.data?.y) return;

                        if (customization.showRawData) {
                            plotData.push({
                                x: sensorData.data.x,
                                y: sensorData.data.y,
                                mode: "markers",
                                type: "scatter",
                                name: `${sensorKey} Error`,
                                marker: {
                                    color: colors[index % colors.length],
                                    size: customization.bubbleSize || 8,
                                    opacity: 0.7,
                                    symbol: customization.markerStyle || "circle",
                                },
                                hoverinfo: customization.hoverInfo || "x+y+name",
                                xaxis: `x${index + 1}`,
                                yaxis: `y${index + 1}`,
                                showlegend: customization.showLegend,
                            });
                        }

                        if (customization.showFitLines) {
                            plotData.push({
                                x: sensorData.data.x,
                                y: sensorData.data.y,
                                mode: "lines",
                                type: "scatter",
                                name: `${sensorKey} Trend`,
                                line: {
                                    color: colors[index % colors.length],
                                    width: 2,
                                    dash: customization.lineStyle === "solid" ? null : customization.lineStyle,
                                    shape: "spline",
                                    smoothing: 1.3,
                                },
                                hoverinfo: customization.hoverInfo || "x+y+name",
                                xaxis: `x${index + 1}`,
                                yaxis: `y${index + 1}`,
                                showlegend: customization.showLegend,
                            });
                        }
                    });

                    const layout = applyTheme(
                        {
                            title: {
                                text: `Faceted Error Graph for ${ measurement.filename || "Measurement" }`,
                                x: 0.5,
                                xanchor: "center",
                            },
                            grid,
                            showlegend: customization.showLegend,
                            margin: { t: 80, b: 100, l: 80, r: 50 },
                            hovermode: "closest",
                        },
                        customization.theme
                    );

                    sensorKeys.forEach((sensorKey, index) => {
                        layout[`xaxis${index + 1}`] = {
                            title: index % columns === 0 ? "Temperature (°C)" : "",
                            showgrid: customization.showGrid,
                            gridcolor: customization.theme === "dark" ? "#444" : "#eee",
                            range: [minXtoShow, maxXtoShow],
                        };
                        layout[`yaxis${index + 1}`] = {
                            title: index < columns ? `Error (mmHg) - ${sensorKey}` : "",
                            showgrid: customization.showGrid,
                            gridcolor:  customization.theme === "dark" ? "#444" : "#eee",
                            range: [minYtoShow, maxYtoShow],
                        };
                    });

                    Plotly.react(plotDivRef.current, plotData, layout, config);
                } else if (customization.graphType === "density") {
                    const columns = customization.densityColumns || 2;
                    const rows = Math.ceil(sensorKeys.length / columns);
                    const grid = { rows, columns, pattern: "independent" };

                    // Calculate density range
                    let globalZmin = Infinity;
                    let globalZmax = -Infinity;
                    Object.values(errorGraphData).forEach((sensorData) => {
                        if (sensorData.data?.y) {
                            const counts = sensorData.data.y.length;
                            globalZmin = Math.min(globalZmin, 0);
                            globalZmax = Math.max(globalZmax, counts);
                        }
                    });

                    sensorKeys.forEach((sensorKey, index) => {
                        const sensorData = errorGraphData[sensorKey];
                        if (!sensorData.data?.x || !sensorData.data?.y) return;

                        const densityType = validDensityTypes.includes( customization.densityType ) ? customization.densityType : "histogram2d";
                        plotData.push({
                            x: sensorData.data.x,
                            y: sensorData.data.y,
                            type: densityType,
                            name: `${sensorKey} Density`,
                            colorscale: getColorPalette(),
                            nbinsx: customization.densityBinSize || 20,
                            nbinsy: customization.densityBinSize || 20,
                            hovertemplate:
                                `<b>Sensor:</b> ${sensorKey}<br>` +
                                `<b>Temperature:</b> %{x}°C<br>` +
                                `<b>Error:</b> %{y}mmHg<br>` +
                                `<b>Count:</b> %{z}<extra></extra>`,
                            showscale: index === 0,
                            zmin: customization.zmin || globalZmin,
                            zmax: customization.zmax || globalZmax,
                            colorbar: {
                                lenmode: "fraction",
                                len: 0.75,
                                thickness: 20,
                                ticks: "outside",
                                tickfont: { size: 12 },
                                title: { text: "Density", side: "right" },
                            },
                            xaxis: `x${index + 1}`,
                            yaxis: `y${index + 1}`,
                        });
                    });

                    const layout = applyTheme(
                        {
                            title: { text: `Error Density Plots for ${ measurement.filename || "Measurement" }`,
                                x: 0.5,
                                xanchor: "center",
                            },
                            grid,
                            showlegend: customization.showLegend,
                            margin: { t: 80, b: 100, l: 80, r: 50 },
                            hovermode: "closest",
                        },
                        customization.theme
                    );

                    sensorKeys.forEach((_, index) => {
                        layout[`xaxis${index + 1}`] = {
                            title: index % columns === 0 ? "Temperature (°C)" : "",
                            showgrid: customization.showGrid,
                            gridcolor: customization.theme === "dark" ? "#444" : "#eee",
                            range: [minXtoShow, maxXtoShow],
                        };
                        layout[`yaxis${index + 1}`] = {
                            title: index < columns ? "Error (mmHg)" : "",
                            showgrid: customization.showGrid,
                            gridcolor: customization.theme === "dark" ? "#444" : "#eee",
                            range: [minYtoShow, maxYtoShow],
                        };
                    });

                    Plotly.react(plotDivRef.current, plotData, layout, config);
                } else {
                    // Scatter plot
                    sensorKeys.forEach((sensorKey, index) => {
                        const sensorData = errorGraphData[sensorKey];
                        if (!sensorData.data?.x || !sensorData.data?.y) return;

                        if (customization.showRawData) {
                            plotData.push({
                                x: sensorData.data.x,
                                y: sensorData.data.y,
                                mode: "markers",
                                type: "scatter",
                                name: `${sensorKey} Error`,
                                marker: {
                                    color: colors[index % colors.length],
                                    size: customization.bubbleSize || 8,
                                    opacity: 0.7,
                                    symbol: customization.markerStyle || "circle",
                                    line: { width: 1, color: customization.theme === "dark" ? "#222" : "#fff",
                                    },
                                },
                                hoverinfo: customization.hoverInfo || "x+y+name",
                            });
                        }

                        if (customization.showFitLines) {
                            plotData.push({
                                x: sensorData.data.x,
                                y: sensorData.data.y,
                                mode: "lines",
                                type: "scatter",
                                name: `${sensorKey} Trend`,
                                line: {
                                    color: colors[index % colors.length],
                                    width: 2,
                                    dash: customization.lineStyle === "solid" ? null : customization.lineStyle,
                                    shape: "spline",
                                    smoothing: 1.3,
                                },
                                hoverinfo: customization.hoverInfo || "x+y+name",
                            });
                        }
                    });

                    const layout = applyTheme(
                        {
                            title: { text: `Error Graph for ${ measurement.filename || "Measurement" }`,
                                x: 0.5,
                                xanchor: "center",
                            },
                            xaxis: {
                                title: "Temperature (°C)",
                                showgrid: customization.showGrid,
                                gridcolor: customization.theme === "dark" ? "#444" : "#eee",
                                range: [minXtoShow, maxXtoShow],
                                tickmode: "auto",
                            },
                            yaxis: {
                                title: "Error (mmHg)",
                                showgrid: customization.showGrid,
                                gridcolor: customization.theme === "dark" ? "#444" : "#eee",
                                range: [minYtoShow, maxYtoShow],
                                rangemode: "tozero",
                            },
                            showlegend: customization.showLegend,
                            margin: { t: 80, b: 100, l: 80, r: 50 },
                            hovermode: "closest",
                            legend: { orientation: "h", y: -0.2 },
                        },
                        customization.theme
                    );

                    Plotly.react(plotDivRef.current, plotData, layout, config);
                }
                setRenderError(null);
            } catch (plotError) {
                setRenderError( "Failed to render the error graph. Please check the data or try again." );
            }
        }, [ errorGraphData, showGraph, measurement?.id, customization, getColorPalette, applyTheme, exportGraph ]);

        if (!measurement?.id) {
            return (
                <Container className="py-4">
                    <Alert variant="warning">  Please select a valid measurement to view the graph. </Alert>
                </Container>
            );
        }

        return (
            <>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h3>{measurement?.filename || "Error Graph"}</h3>
                    <div className="d-flex align-items-center gap-3">
                        <Form.Group controlId="degreeSelect" className="mb-0">
                            <Form.Label className="me-2 mb-0"> Polynomial Degree </Form.Label>
                            <Form.Select size="sm" value={degree} onChange={handleDegreeChange} style={{ width: "80px" }} >
                                <option value="1">1</option>
                                <option value="2">2</option>
                                <option value="3">3</option>
                                <option value="4">4</option>
                            </Form.Select>
                        </Form.Group>
                     </div>
                </div>

                {authError && (
                    <Alert variant="danger" className="mb-4"> {authError} </Alert>
                )}
                {error && (
                    <Alert variant="danger" className="mb-4">
                        {error}
                        <Button variant="outline-danger" size="sm" onClick={onRetry} className="ms-3" > {" "} Retry{" "} </Button>
                    </Alert>
                )}
                {renderError && (
                    <Alert variant="danger" className="mb-4">
                        {renderError}
                        <Button variant="outline-danger" size="sm" onClick={onRetry} className="ms-3" > {" "} Retry{" "} </Button>
                    </Alert>
                )}

                {!loading && !error && !authError && !renderError && (
                    <>
                        <div id="error-plotly-div" ref={plotDivRef} style={{ minHeight: "800px", width: "100%" }} />
                        {errorGraphData && (
                            <Row className="mt-3 gx-3 gy-3">
                                {Object.entries(errorGraphData).map(
                                    ([sensorId, sensorData]) => {
                                        const errorValues = sensorData.data?.y?.filter((y) => !isNaN(y)) || [];
                                        const minError = errorValues.length > 0 ? Math.min(...errorValues) : 0;
                                        const maxError = errorValues.length > 0 ? Math.max(...errorValues) : 0;

                                        return (
                                            <Col key={sensorId} xs={12} md={4} lg={3} >
                                                <Card className="h-100 shadow-sm">
                                                    <Card.Body>
                                                        <Card.Title className="text-primary fs-6"> {sensorId} </Card.Title>
                                                        <ListGroup variant="flush">
                                                            <ListGroup.Item className="text-muted small"> {" "} Min Error:{" "} {minError.toFixed(4)}{" "} mmHg{" "} </ListGroup.Item>
                                                            <ListGroup.Item className="text-muted small"> {" "} Max Error:{" "} {maxError.toFixed(4)}{" "} mmHg{" "} </ListGroup.Item>
                                                        </ListGroup>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        );
                                    }
                                )}
                            </Row>
                        )}
                        {errorGraphData && (
                            <div className="text-muted small mt-3">
                                Showing error data for{" "}
                                {Object.keys(errorGraphData).length} sensors |
                                Data points:{" "}
                                {Object.values(errorGraphData).reduce(
                                    (sum, sensor) => sum + (sensor.data?.x?.length || 0), 0
                                )}
                            </div>
                        )}
                    </>
                )}
            </>
        );
    }
);

export default ErrorGraph;